/* eslint-disable no-nested-ternary */
import type {
  TPropertyTypeV2,
  IPropertyDetail,
  IPropertyPerformance
} from '../../interfaces/src'
import { ROUTES } from '../../web-components/constants/routes'
import { PROPERTY_TYPE_DESCRIPTION } from '../../web-components/utils/properties'

const PUBLIC_DETAIL_URL = process.env.PUBLIC_DETAIL_URL

interface BuildTitle {
  propertyType: TPropertyTypeV2
  neighborhood: string
  saleActive: boolean | null
  presaleActive: boolean | null
  leaseActive: boolean | null
}

export const buildTitle = ({
  propertyType,
  neighborhood,
  saleActive,
  presaleActive,
  leaseActive
}: BuildTitle) => {
  const operations = []
  if (presaleActive) {
    operations?.push('Preventa')
  }
  if (saleActive) {
    operations?.push('Venta')
  }
  if (leaseActive) {
    operations?.push('Renta')
  }

  return `${PROPERTY_TYPE_DESCRIPTION[propertyType]} en ${operations?.join(
    ' y '
  )} en ${neighborhood}`
}

export const generateUrlShareProperty = ({
  property,
  profile,
  isEnable = false
}: {
  property: Partial<
    Pick<
      IPropertyDetail,
      | 'propertyId'
      | 'legacyId'
      | 'leaseActive'
      | 'presaleActive'
      | 'propertyType'
      | 'saleActive'
      | 'neighborhood'
    >
  >
  profile?: any
  isEnable?: boolean
}) => {
  const legacyId = property?.propertyId ?? property?.legacyId
  const buildTitleString = buildTitle({
    leaseActive: property?.leaseActive ?? null,
    neighborhood: property?.neighborhood ?? '',
    presaleActive: property?.presaleActive ?? null,
    propertyType: property?.propertyType ?? 'OTHER',
    saleActive: property?.saleActive ?? null
  })
    ?.replace(/[\s/]/g, '-')
    ?.replace(/[^\w\d\s-]/g, '')
    ?.normalize('NFD')
    ?.replace(/[\u0300-\u036f]/g, '')
    ?.toLocaleLowerCase()
  if (profile && profile?.domain && isEnable) {
    return `${profile?.domain}/detalle-de-propiedad/${buildTitleString}-${legacyId}`
  }
  if (profile && profile?.subdomain && isEnable) {
    return `${profile?.subdomain}.wiggot/detalle-de-propiedad/${buildTitleString}-${legacyId}`
  }
  return `${window.location.origin}/property/${buildTitleString}/${legacyId}`
}

export const generateUrlPublicDatasheet = ({
  property
}: {
  property: IPropertyDetail
}) => {
  return `${window.location.origin}/property/${buildTitle({
    leaseActive: property?.leaseActive,
    neighborhood: property?.neighborhood,
    presaleActive: property?.presaleActive,
    propertyType: property?.propertyType,
    saleActive: property?.saleActive
  })
    ?.replace(/[\s/]/g, '-')
    ?.replace(/[^\w\d\s-]/g, '')
    ?.normalize('NFD')
    ?.replace(/[\u0300-\u036f]/g, '')
    ?.toLocaleLowerCase()}/${property?.propertyId}`
}

export const generateUrlPublicDatasheetV2 = ({
  property
}: {
  property: IPropertyDetail
}) => {
  return `${PUBLIC_DETAIL_URL}/property/${buildTitle({
    leaseActive: property?.leaseActive,
    neighborhood: property?.neighborhood,
    presaleActive: property?.presaleActive,
    propertyType: property?.propertyType,
    saleActive: property?.saleActive
  })
    ?.replace(/[\s/]/g, '-')
    ?.replace(/[^\w\d\s-]/g, '')
    ?.normalize('NFD')
    ?.replace(/[\u0300-\u036f]/g, '')
    ?.toLocaleLowerCase()}/${property?.propertyId}`
}

export const generateUrlPropertyLeads = ({
  property,
  performance
}: {
  property: IPropertyDetail
  performance: IPropertyPerformance
}) => {
  const portalLeads = performance?.portalLeads?.count ?? 0
  const websiteLeads = performance?.websiteLeads?.count ?? 0
  const wiggotLeads = performance?.wiggotLeads?.count ?? 0
  const wiggotBoostLeads = performance?.wiggotBoostLeads?.count ?? 0
  const paramPosition = portalLeads
    ? 'PORTALS'
    : websiteLeads
    ? 'WEB_SITE'
    : wiggotLeads
    ? 'WIGGOT_LEADS'
    : wiggotBoostLeads
    ? 'WIGGOT'
    : ''
  const paramYear = portalLeads
    ? performance?.portalLeads?.lastLeadYear
    : websiteLeads
    ? performance?.websiteLeads?.lastLeadYear
    : wiggotLeads
    ? performance?.wiggotLeads?.lastLeadYear
    : wiggotBoostLeads
    ? performance?.wiggotBoostLeads?.lastLeadYear
    : ''
  const paramTable = property?.agencyOwned
    ? 'AGENCY_PROPERTIES'
    : 'MY_PROPERTIES'

  return `${ROUTES.LEADS}?propertyId=${property?.propertyId}&position=${paramPosition}&fYear=${paramYear}&fTable=${paramTable}`
}

export const getMatterPortThumbnail = (matterportUrl: string) => {
  let matterportThumbnail = ''

  if (matterportUrl) {
    const lastIndex = matterportUrl?.lastIndexOf('/?')
    const queryString = matterportUrl?.slice(lastIndex + 2)
    const parseQueryString = (query: string) => {
      return query
        ?.split('&')
        .reduce((acc: { [key: string]: string }, param) => {
          const [key, value] = param.split('=')
          acc[key] = decodeURIComponent(value)
          return acc
        }, {})
    }

    const matterPortParams = parseQueryString(queryString)

    if (matterPortParams?.m) {
      matterportThumbnail = `https://my.matterport.com/api/v1/player/models/${matterPortParams?.m}/thumb`
    }
  }

  return matterportThumbnail
}
